import { type ReactNode, Suspense, lazy, useCallback } from "react";

import {
  displayPathOf,
  isBufferDirty,
  useEditorStore,
  type CenterTabId,
} from "~/editor/editorStore";

import { EditorTab } from "./EditorTab";

/**
 * CodeMirror and its language grammars are a large dependency that only matters
 * once a file tab is open. Loading it eagerly would put the whole editor in the
 * entry chunk for every visitor, including phones that never open one.
 */
const EditorPanel = lazy(() =>
  import("./EditorPanel").then((module) => ({ default: module.EditorPanel })),
);

export interface CenterTabsProps {
  /**
   * The chat view rendered inside the "Chat" tab. Always mounted (kept alive
   * via CSS visibility) so chat state survives tab switches.
   */
  readonly chat: ReactNode;
  /**
   * Whether the current user can save edits. Drives editor read-only mode
   * and the visibility of the Save button.
   */
  readonly canEdit?: boolean;
}

const fileNameOf = (relativePath: string): string => {
  const slash = relativePath.lastIndexOf("/");
  return slash >= 0 ? relativePath.slice(slash + 1) : relativePath;
};

export function CenterTabs(props: CenterTabsProps) {
  const buffers = useEditorStore((state) => state.buffers);
  const activeTab = useEditorStore((state) => state.activeTab);
  const setActiveTab = useEditorStore((state) => state.setActiveTab);
  const closeBuffer = useEditorStore((state) => state.closeBuffer);

  const isChatActive = activeTab === "chat";
  const activeBufferId = typeof activeTab === "string" ? null : activeTab.bufferId;

  const handleActivate = useCallback(
    (tab: CenterTabId) => {
      setActiveTab(tab);
    },
    [setActiveTab],
  );

  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col">
      {buffers.length > 0 ? (
        <div className="flex shrink-0 items-stretch border-b border-border bg-card text-card-foreground">
          <EditorTab
            label="Chat"
            isActive={isChatActive}
            onActivate={() => handleActivate("chat")}
          />
          {buffers.map((buffer) => (
            <EditorTab
              key={buffer.bufferId}
              label={fileNameOf(displayPathOf(buffer))}
              title={displayPathOf(buffer)}
              isActive={activeBufferId === buffer.bufferId}
              isDirty={buffer.status === "ready" ? isBufferDirty(buffer) : false}
              hasExternalChange={buffer.status === "ready" ? buffer.hasExternalChange : false}
              onActivate={() => handleActivate({ kind: "buffer", bufferId: buffer.bufferId })}
              onClose={() => {
                if (
                  buffer.status === "ready" &&
                  isBufferDirty(buffer) &&
                  !window.confirm(`Discard unsaved changes to ${displayPathOf(buffer)}?`)
                ) {
                  return;
                }
                closeBuffer(buffer.bufferId);
              }}
            />
          ))}
        </div>
      ) : null}

      <div className="relative min-h-0 flex-1">
        <div
          className="absolute inset-0 flex min-h-0 min-w-0 flex-col"
          style={isChatActive ? undefined : { display: "none" }}
        >
          {props.chat}
        </div>
        {!isChatActive
          ? buffers
              .filter((buffer) => buffer.bufferId === activeBufferId)
              .map((buffer) => (
                <div
                  key={buffer.bufferId}
                  className="absolute inset-0 flex min-h-0 min-w-0 flex-col"
                >
                  <Suspense fallback={null}>
                    <EditorPanel buffer={buffer} canEdit={props.canEdit ?? false} />
                  </Suspense>
                </div>
              ))
          : null}
      </div>
    </div>
  );
}
